import { message } from "antd";
import { type CheckboxChangeEvent } from "antd/lib/checkbox";
import { type SorterResult } from "antd/lib/table/interface";
import moment from "moment";
import { PureComponent } from "react";

import { CheckAllState, type JobStatusTypes } from "types/enum";

import { isNullOrUndefined } from "utilities/object/object";
import { deepClone } from "utilities/object/object";
import { dateSorter, lexicographicSorter } from "utilities/sort/sort";

import { BTCheckbox } from "commonComponents/btWrappers/BTCheckbox/BTCheckbox";
import { BTTable, type IBTColumnProps } from "commonComponents/btWrappers/BTTable/BTTable";
import { getCheckAllState } from "commonComponents/btWrappers/BTTable/BTTable.utils";
import { FilteredEmptyState } from "commonComponents/entity/emptyState/FilteredEmptyState";
import {
    type IJobAccess,
    type JobAccessEntity,
    JobStatusFilterTypes,
} from "commonComponents/entity/jobAccess/JobAccess/JobAccess.api.types";
import {
    mapJobStatusFilterTypesToJobStatusType,
    mapJobStatusFilterTypesToString,
    mapJobStatusTypesToFilterTypes,
} from "commonComponents/entity/jobAccess/JobAccess/JobAccess.utils";
import { DateDisplay } from "commonComponents/utilities/DateDisplay/DateDisplay";
import { getStatusTypeForJob } from "commonComponents/utilities/JobPicker/JobPicker.utils";
import { StatusTagDisplay } from "commonComponents/utilities/Status/StatusTagDisplay";

import { type JobsiteGroup } from "entity/internalUser/InternalUserDetails/InternalUser.api.types";

export interface IJobAccessProps {
    jobAccessEntity: JobAccessEntity;
    disabled?: boolean;
    jobGroups?: JobsiteGroup[];
    isSelf?: boolean;
    inEditMode?: boolean;
    setFieldValue: (field: string, value: any) => void;
    onJobAccessChanged?: (jobAccess: IJobAccess) => void;
    onToggleAllJobAccess?: (jobAccess: IJobAccess[], checked: boolean) => void;
    sortSettings?: SorterResult<IJobAccess>;
    onChangeSortSettings?: (sorter: SorterResult<IJobAccess> | SorterResult<IJobAccess>[]) => void;
    roleJobStatusAccess?: JobStatusTypes[];
}

export class JobAccessContainer extends PureComponent<IJobAccessProps> {
    static defaultProps = {
        sortSettings: {
            order: "descend",
            columnKey: "dateCreated",
            field: "dateCreated",
        } as SorterResult<IJobAccess>,
        onChangeSortSettings: () => {},
    };

    private getColumns = () => {
        const { jobAccessEntity, disabled, isSelf, inEditMode, roleJobStatusAccess, jobGroups } =
            this.props;
        const cols: IBTColumnProps<IJobAccess>[] = [];
        const hasNotificationAccessColumn = jobAccessEntity.jobs.some(
            (job) => !isNullOrUndefined(job.hasNotificationAccess),
        );
        const hasAccessCheckedState = getCheckAllState(jobAccessEntity.jobs, "hasAccess");
        const hasNotificationAccessCheckedState = this.getNotificationCheckedAllState(
            jobAccessEntity.jobs,
        );
        // Needed to still disable notification column in view state
        const disableSelfNotificationAccess = disabled && isSelf && !inEditMode;

        let jobStatuses: JobStatusFilterTypes[] = [];
        const checkJobStatus = roleJobStatusAccess !== undefined;
        if (checkJobStatus) {
            jobStatuses = roleJobStatusAccess.map((status) =>
                mapJobStatusTypesToFilterTypes(status),
            );
            // Always add templates since their checkbox won't be disabled
            jobStatuses.push(JobStatusFilterTypes.Templates);
        }

        cols.push({
            title: (
                <>
                    {hasNotificationAccessColumn ? "Access" : ""}
                    <BTCheckbox
                        id="hasAccessSelectAll"
                        data-testid="chkHasAccessSelectAll"
                        checked={hasAccessCheckedState === CheckAllState.AllChecked}
                        indeterminate={hasAccessCheckedState === CheckAllState.Indeterminate}
                        onChange={(e) =>
                            this.handleJobAccessSelectAll(e, jobAccessEntity.jobs, jobStatuses)
                        }
                        disabled={disabled}
                        className={`${
                            hasNotificationAccessColumn ? "CheckboxWithTitle" : "CheckboxOnly"
                        }`}
                    />
                </>
            ),
            dataIndex: "hasAccess",
            render: (text, record) => (
                <BTCheckbox
                    id="hasAccess"
                    data-testid="chkHasAccess"
                    checked={record.hasAccess}
                    disabled={
                        disabled || (checkJobStatus && !jobStatuses.includes(record.jobStatus))
                    }
                    onChange={(e) => this.handleJobAccessChanged(e, record)}
                />
            ),
            align: "center",
        });
        hasNotificationAccessColumn &&
            cols.push({
                dataIndex: "hasNotificationAccess",
                title: (
                    <>
                        Notifications
                        <BTCheckbox
                            id="hasNotificationAccessSelectAll"
                            data-testid="chkHasNotificationAccessSelectAll"
                            checked={hasNotificationAccessCheckedState === CheckAllState.AllChecked}
                            indeterminate={
                                hasNotificationAccessCheckedState === CheckAllState.Indeterminate
                            }
                            onChange={(e) =>
                                this.handleJobNotificationAccessSelectAll(e, jobAccessEntity.jobs)
                            }
                            disabled={(disabled && !isSelf) || disableSelfNotificationAccess}
                            className="CheckboxWithTitle"
                        />
                    </>
                ),
                align: "center",
                render: (text, record) => (
                    <BTCheckbox
                        id="hasNotificationAccess"
                        data-testid="chkHasNotificationAccess"
                        checked={record.hasNotificationAccess!}
                        onChange={(e) => this.handleNotificationAccessChanged(e, record)}
                        disabled={
                            ((!record.hasAccess || disabled) && !isSelf) ||
                            (checkJobStatus && !jobStatuses.includes(record.jobStatus)) ||
                            disableSelfNotificationAccess
                        }
                    />
                ),
            });
        cols.push({
            title: "Job name",
            dataIndex: "jobName",
            sortDirections: ["ascend", "descend", "ascend"],
            sorter: (a, b) => lexicographicSorter(a.jobName, b.jobName),
            align: "left",
        });
        cols.push({
            title: "Job status",
            dataIndex: "jobStatus",
            sortDirections: ["ascend", "descend", "ascend"],
            sorter: (a, b, order) => {
                // Sort by Job Status. If comparable same, next compare JobName
                const n = lexicographicSorter(
                    JobStatusFilterTypes[a.jobStatus],
                    JobStatusFilterTypes[b.jobStatus],
                );
                if (n === 0) {
                    return lexicographicSorter(
                        order === "descend" ? b.jobName : a.jobName,
                        order === "descend" ? a.jobName : b.jobName,
                    );
                }
                return n;
            },
            render: (_, record) => (
                <StatusTagDisplay
                    statusText={mapJobStatusFilterTypesToString(record.jobStatus)}
                    statusType={getStatusTypeForJob(
                        mapJobStatusFilterTypesToJobStatusType(record.jobStatus),
                    )}
                />
            ),
            align: "left",
        });
        if (jobGroups) {
            cols.push({
                title: "Group",
                dataIndex: "group",
                sortDirections: ["ascend", "descend", "ascend"],
                sorter: (a, b) => {
                    return lexicographicSorter(a.groupNames?.[0], b.groupNames?.[0]);
                },
                render: (_, record) => {
                    return record.groupNames!.join(", ");
                },
            });
        }
        cols.push({
            title: "Date opened",
            dataIndex: "dateCreated",
            sortDirections: ["ascend", "descend", "ascend"],
            sorter: (a, b, order) => {
                // Sort by Date Created date. If comparable same, next compare JobName
                const aDate = moment(a.dateCreated).dateOnly();
                const bDate = moment(b.dateCreated).dateOnly();
                const n = dateSorter(aDate, bDate);
                if (n === 0) {
                    return lexicographicSorter(
                        order === "descend" ? b.jobName : a.jobName,
                        order === "descend" ? a.jobName : b.jobName,
                    );
                }
                return n;
            },
            render: (_, record) => <DateDisplay value={moment(record.dateCreated)} />,
            align: "left",
        });
        this.handleColSorting(cols);
        return cols;
    };

    private handleJobAccessChanged = (e: CheckboxChangeEvent, row: IJobAccess) => {
        const { jobAccessEntity, setFieldValue, onJobAccessChanged } = this.props;
        const newJobAccess = deepClone(jobAccessEntity);
        newJobAccess.jobs
            .filter((job) => job.jobId === row.jobId)
            .map((j) => {
                j.hasAccess = e.target.checked;
                if (j.hasNotificationAccess !== undefined) {
                    j.hasNotificationAccess = e.target.checked;
                }
                return j;
            });

        onJobAccessChanged && onJobAccessChanged(row);

        setFieldValue("jobAccess", newJobAccess);
    };

    private handleJobAccessSelectAll = (
        e: CheckboxChangeEvent,
        col: IJobAccess[],
        jobStatuses: JobStatusFilterTypes[],
    ) => {
        const { setFieldValue, roleJobStatusAccess, onToggleAllJobAccess } = this.props;
        let updatedJobCount = 0;
        const checkJobStatus = roleJobStatusAccess !== undefined;

        const newJobAccess = {
            jobs: col.map((job) => {
                const previouslyHadAccess = job.hasAccess;
                job.hasAccess = checkJobStatus
                    ? e.target.checked && jobStatuses.includes(job.jobStatus)
                    : e.target.checked;
                if (!previouslyHadAccess && job.hasAccess) {
                    updatedJobCount++;
                }
                if (job.hasNotificationAccess !== undefined) {
                    job.hasNotificationAccess = e.target.checked && job.hasAccess;
                }
                return job;
            }),
        };
        void message.info(
            `Action applied to ${updatedJobCount} ${
                updatedJobCount === 1 ? "Job" : "Jobs"
            } in this filter`,
        );
        onToggleAllJobAccess && onToggleAllJobAccess(col, e.target.checked);

        setFieldValue("jobAccess", newJobAccess);
    };

    private handleJobNotificationAccessSelectAll = (e: CheckboxChangeEvent, col: IJobAccess[]) => {
        const { setFieldValue } = this.props;
        const newJobNotification = {
            jobs: col.map((job) => ({
                ...job,
                hasNotificationAccess: job.hasAccess ? e.target.checked : false,
            })),
        };
        setFieldValue("jobAccess", newJobNotification);
    };

    private handleNotificationAccessChanged = (e: CheckboxChangeEvent, row: IJobAccess) => {
        const { jobAccessEntity, setFieldValue } = this.props;
        const newJobNotification = deepClone(jobAccessEntity);
        newJobNotification.jobs
            .filter((job) => job.jobId === row.jobId)
            .map((job) => {
                if (job.hasAccess) {
                    job.hasNotificationAccess = e.target.checked;
                }
                return job;
            });
        setFieldValue("jobAccess", newJobNotification);
    };

    private getNotificationCheckedAllState = (rows: IJobAccess[]) => {
        if (rows.some((row) => row.hasNotificationAccess)) {
            if (rows.every((row) => row.hasNotificationAccess || !row.hasAccess)) {
                return CheckAllState.AllChecked;
            }
            return CheckAllState.Indeterminate;
        }
        return CheckAllState.None;
    };

    private handleColSorting = (cols: IBTColumnProps<IJobAccess>[]) => {
        const { sortSettings } = this.props;
        const sortedCols = cols.filter((col) => col.dataIndex === sortSettings!.columnKey);
        if (sortedCols.length) {
            sortedCols[0].defaultSortOrder = sortSettings!.order;
        }
    };

    render() {
        const { jobAccessEntity } = this.props;

        return (
            <>
                {jobAccessEntity.jobs.length > 0 && (
                    <BTTable
                        rowKey={(job) => job.jobId.toString()}
                        dataSource={jobAccessEntity.jobs}
                        columns={this.getColumns()}
                        pagination={{
                            position: ["bottomRight"],
                            defaultPageSize: 50,
                            hideOnSinglePage: true,
                            responsive: true,
                        }}
                        emptyState={false}
                        className="SelectAllRowsTable"
                        onChange={(_pagination, _filters, sorter, _extra) => {
                            this.props.onChangeSortSettings!(sorter);
                        }}
                        scroll={{
                            x: true,
                        }}
                    />
                )}
                {jobAccessEntity.jobs.length === 0 && (
                    <FilteredEmptyState entityTitlePlural="Jobs" />
                )}
            </>
        );
    }
}
